import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createTask, createAssignment, type Employee, type Client } from '../../lib/api'
import { useDisciplines } from '../../hooks/useDisciplines'
import { X } from 'lucide-react'

interface QuickAddProps {
  onClose: () => void
  employees?: Employee[]
  clients?: Client[]
  /** Shows the assignee picker. `roleCanWrite`. */
  canAssign?: boolean
  /** Switches to the full New task form, carrying the typed title across (LXP-45). */
  onExpand?: (title: string) => void
}

export function QuickAdd({ onClose, employees = [], clients = [], canAssign = true, onExpand }: QuickAddProps) {
  const qc = useQueryClient()
  const disciplines = useDisciplines()
  const inputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState('med')
  const [discipline, setDiscipline] = useState(disciplines[0] ?? 'ops')
  const [due, setDue] = useState('')
  const [clientId, setClientId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')

  const mutation = useMutation({
    mutationFn: async () => {
      const task = await createTask({
        title: title.trim(),
        priority,
        discipline,
        due: due || undefined,
        clientId: clientId || undefined,
      })
      if (assigneeId) {
        // Task assignment fires the assignee email server-side. Don't let a
        // failed assignment lose the created task.
        await createAssignment({
          employeeId: assigneeId,
          entityType: 'task',
          entityId: task.id,
          role: 'lead',
        }).catch(() => undefined)
      }
      return task
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['assignments'] })
      setTitle('')
      setAssigneeId('')
      inputRef.current?.focus()
    },
  })

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Focus on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    mutation.mutate()
  }

  return (
    <div className="quick-add-backdrop" onClick={onClose}>
      <div className="quick-add" onClick={(e) => e.stopPropagation()}>
        <div className="quick-add-header">
          <h3>Quick add task</h3>
          <button onClick={onClose} className="btn-icon" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="quick-add-form">
          <input
            ref={inputRef}
            type="text"
            className="quick-add-input"
            placeholder="Task title (Enter to add, keep typing for more)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <div className="quick-add-options">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Priority">
              <option value="low">Low</option>
              <option value="med">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} aria-label="Discipline">
              {disciplines.map((d) => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
              ))}
            </select>
            {clients.length > 0 && (
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Client">
                <option value="">No client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            {canAssign && employees.length > 0 && (
              <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} aria-label="Assignee">
                <option value="">Unassigned</option>
                {employees
                  .filter((e) => e.active)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            )}
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              aria-label="Due date"
            />
            <button type="submit" className="btn btn-primary btn-sm" disabled={mutation.isPending}>
              Add
            </button>
          </div>
          {onExpand && (
            <button type="button" className="quick-add-more" onClick={() => onExpand(title)}>
              Need a description, files or links? Open the full form
            </button>
          )}
        </form>
        {mutation.isSuccess && (
          <p className="text-muted" style={{ fontSize: 12, marginTop: 'var(--space-1)' }}>
            Task added. Keep going or press Esc to close.
          </p>
        )}
      </div>
    </div>
  )
}
